/**
 * @ngdoc service
 * @name authService
 * @module flowingly.runner.services
 *
 * @description A helper service for authenticating the user.
 *
 * ## Notes
 *
 * ###API
 * * userProfile - retunrs the users stored profile
 * * login - log the user in
 * * logout - log the user out
 * * registerAuthenticationListener - register callback for Auth0 login
 *
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import { IFlowinglyWindow } from '@Client/interfaces/flowingly.window';
import IUserTokenResponse from '@Shared.Angular/@types/core/contracts/queryModel/api/userTokenResponse';
import IUserDetail from '@Shared.Angular/@types/core/contracts/queryModel/user/userDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import type {
  UserConditionV2Detail,
  UserConditionsV2Response
} from '@Shared.Angular/@types/core/contracts/queryModel/userCondition/userConditionV2';
import {
  LoginAuditAction,
  LoginAuditIdentityProvider
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { ILocalUserDetail } from '@Shared.Angular/flowingly.services/sessionService';
import angular, {
  IHttpService,
  IQService,
  IRootScopeService,
  IWindowService
} from 'angular';
import { IStateService } from 'angular-ui-router';
import { Auth0UserProfile } from 'auth0-js';

type LoginOptions = {
  auth0UseUniversalLogin: boolean;
  logoUrl: string;
  welcomeText: string;
  loginAlertMessage: string;
  favIcon: string;
  redirectUrl?: string;
  runnerTitle: string;
};

export interface BusinessTenant {
  id: Guid;
  name: string;
}

declare const window: IFlowinglyWindow;

angular.module('flowingly.runner.services').factory('authService', authService);

authService.$inject = [
  '$rootScope',
  '$q',
  '$http',
  '$window',
  '$location',
  'dialogService',
  'sessionService',
  'tokenService',
  'APP_CONFIG',
  'sideMenuService',
  'intercomService',
  'lock',
  'angularAuth0',
  'authManager',
  '$state',
  'browserUtilsService',
  'userApiService',
  'lodashService',
  'authLoggingApiService',
  'angularAuth0SpaSdk',
  'iframePopupAuthService'
];

function authService(
  $rootScope: IRootScopeService,
  $q: IQService,
  $http: IHttpService,
  $window: IWindowService,
  $location: angular.ILocationService,
  dialogService: SharedAngular.DialogService,
  sessionService: SharedAngular.SessionService,
  tokenService: SharedAngular.TokenService,
  APP_CONFIG: SharedAngular.APP_CONFIG,
  sideMenuService: SideMenuService,
  intercomService: SharedAngular.IntercomService,
  lock: AngularLock,
  angularAuth0: auth0.WebAuth,
  authManager: angular.jwt.IAuthManagerServiceProvider,
  $state: IStateService,
  browserUtilsService: SharedAngular.BrowserUtilsService,
  userApiService: SharedAngular.UserApiService,
  lodashService: Lodash,
  authLoggingApiService: SharedAngular.AuthLoggingApiService,
  angularAuth0SpaSdk: SharedAngular.AngularAuth0SpaSdk,
  iframePopupAuthService: IframePopupAuthService
) {
  //See for more details
  //https://github.com/auth0-samples/auth0-angularjs-sample/blob/ui-router-html5/08-Calling-Api/public/components/auth/auth.service.js

  const userProfileJson = sessionService.getProfile();
  let userProfile = (userProfileJson && JSON.parse(userProfileJson)) || null;
  let user = sessionService.getUser();
  let redirectUrl = '';

  const deferredProfile = $q.defer<Auth0UserProfile>();
  let deferredUser = $q.defer<ILocalUserDetail>();

  if (userProfile) {
    deferredProfile.resolve(userProfile);
  }
  if (user) {
    deferredUser.resolve(user);
  }

  const service = {
    getUser: getUser,
    getUserDetails: getUserDetails,
    userProfile: userProfile,
    getProfileDeferred: getProfileDeferred,
    getUserPendingAcceptCondition: getUserPendingAcceptCondition,
    getUserDeferred: getUserDeferred,
    isAuthenticated: isAuthenticated,
    isWorkflowUser: isWorkflowUser,
    login: login,
    logout: logout,
    loginWithToken: loginWithToken,
    setUserAfterSuccessfulLogin: loginSuccessful,
    registerAuthenticationListener: registerAuthenticationListener,
    getTenants: getTenants,
    changeTenant: changeTenant,
    getLocalOnlyToken: getLocalOnlyToken,
    loginFailed: loginFailed,
    auth0SpaSdkAuthenticated: auth0SpaSdkAuthenticated
  };

  return service;

  //////////// Public API Methods

  // When the user logs on he/she is authenticated by Auth0. This happens before
  // we go and retrieve the user details from the API. Once we integrate our User properly
  // with the Auth0 User then this distinctioon will go away (TODO).
  function isAuthenticated() {
    return $rootScope.isAuthenticated;
  }

  function isWorkflowUser() {
    const permissions = sessionService.getPermissions();

    let setupPermission = false;
    lodashService.forEach(permissions, (permission) => {
      if (permission && permission.toLowerCase().indexOf('setup.') > -1) {
        setupPermission = true;
      }
    });

    return !setupPermission;
  }

  async function login(options: LoginOptions) {
    const {
      auth0UseUniversalLogin,
      logoUrl,
      welcomeText,
      loginAlertMessage,
      favIcon,
      redirectUrl,
      runnerTitle
    } = options;

    userProfile = null;
    user = null;
    sessionService.clearSessionAndToken('authService.login');
    authManager.unauthenticate();

    authLoggingApiService.log('Login box is about to be displayed.');

    if (auth0UseUniversalLogin) {
      const params = {
        logo: logoUrl,
        welcomeText: welcomeText,
        loginAlertMessage,
        favIcon,
        state: redirectUrl,
        title: runnerTitle
      };
      if (APP_CONFIG.auth0UseSilentSso) {
        const lastlogin = tokenService.getLastLogin();
        if (lastlogin && lastlogin.connection) {
          params['connection'] = lastlogin.connection;
        }
      }
      if (APP_CONFIG.auth0UseRefreshToken) {
        // Check if we should use popup login for iframe context
        if (iframePopupAuthService.shouldUsePopupLogin()) {
          authLoggingApiService.log(
            'auth.service login() - Using popup login for iframe context'
          );
          try {
            const tokens = await iframePopupAuthService.openLoginPopup(
              redirectUrl
            );
            await handleIframePopupTokens(tokens, redirectUrl);
          } catch (error) {
            authLoggingApiService.log(
              `auth.service login() - Popup login failed: ${error.message}`
            );
            // Show error to user
            dialogService.showMessageDialog({
              headerText: 'Login Error',
              message: error.message || 'Login failed. Please try again.'
            });
          }
          return;
        }

        const snakeCaseParams = lodashService.mapKeys(params, (value, key) =>
          lodashService.snakeCase(key)
        );
        const auth0Client = await angularAuth0SpaSdk.getClient();
        auth0Client.loginWithRedirect({
          authorizationParams: snakeCaseParams,
          appState: { redirectUrl: redirectUrl }
        });
      } else {
        angularAuth0.authorize(params);
      }
    } else {
      // For Auth0 Lock 10 (eg to logon):
      // See Auth0 Lock and doco here: https://github.com/auth0/lock
      // For customisation options => : https://auth0.com/docs/libraries/lock/v10/customization
      // And see https://auth0.com/docs/libraries/lock/v10/sending-authentication-parameters

      // For access to the full autho API (eg to refresh a token):
      // So basically we follow this doco: https://github.com/auth0/auth0.js
      // but we use this library https://github.com/auth0/angular-auth0
      // and we be careful to NOT use auth0-angular as it is for lock 9.

      // And to work with JWTs (eg to see if they have expired):
      // See https://github.com/auth0/angular-jwt

      //setting redirect url only when user is coming from email or report page
      //Though for such urls redirect happens twice, once after auth0 authorization application is redirected
      //and again as request pipeline, auth0 again checks redirected url and finds valid token and shows the page
      //I was trying to stop second redirect because auth0 has already performed authentication during login but
      //couldn't do so as I can't use authManger service to check if use authenticated and $rootscope etc.
      let redirectUrl = '';
      const domainPort =
        window.location.port !== '' ? `:${window.location.port}` : '';
      const currentDomain = `${window.location.protocol}//${window.location.hostname}${domainPort}`;

      const currentUrl = window.location.href;
      const splitString = 'redirectUrl=';
      if (
        (currentUrl.indexOf(encodeURIComponent('/flows/')) > 0 ||
          currentUrl.indexOf(encodeURIComponent('/categoryId?')) > 0 ||
          currentUrl.indexOf(encodeURIComponent('/processmapsv2/')) > 0 ||
          currentUrl.indexOf(encodeURIComponent('/processmaps/')) > 0) &&
        currentUrl.indexOf('access_token=') < 0
      ) {
        const redirectUrlIndex = currentUrl.indexOf(splitString);
        const substringAfterRedirectUrl = currentUrl.substring(
          redirectUrlIndex + splitString.length
        );
        redirectUrl = substringAfterRedirectUrl;
      }

      // Determine if the app is inside an iframe
      const isInIframe = window !== window.parent;
      let callbackRedirectUri = `${currentDomain}/flowsactive/`;
      if (isInIframe) {
        callbackRedirectUri = `${currentDomain}/iframe/`;
      }

      const authOptions = {
        allowSignUp: false,
        // TODO: FLOW-1214 Disable thid for now to prevent the incorrect user across multiple environments bug
        rememberLastLogin: false,
        avatar: null,
        theme: {
          // TODO: We are using our small logo as the large logo is rendered badly. We could overide the style lock uses for the logo
          // but the auth0 lock doco says clearly that this is not a good thing to do. So, rather we need to create a logo of the right dimensions
          // I believe. Nick has seen an example where a logo such as ours is used. So if we cant get this working then we can ask the question
          // on the auth0 forms.
          logo: APP_CONFIG.logoUrl,
          primaryColor: '#4A90E2'
        },
        closable: false,
        //container: 'hiw-login-container',
        languageDictionary: {
          title: APP_CONFIG.welcomeText, // TODO: We would rather remove the title field all together. I have not worked out how to do that. Will ask on the Auth0 forums.
          usernameOrEmailInputPlaceholder: 'your email'
        },
        sso: true,
        auth: {
          params: {
            scope:
              'openid email offline_access nameidentifier businessidentifier flowinglypermissions', //offline_access so that we get a refresh token
            redirectUri: callbackRedirectUri,
            // state param is returned as is from auth0 service and can be used to send any static data to and fro auth0
            // see more here https://auth0.com/docs/tutorials/redirecting-users
            // though tutorial shows that we can pass object to state but in our case it accepts only string, may be different auth0 APIs
            state: redirectUrl
          }
        },
        _enableIdPInitiatedLogin: true
      };
      lock = new Auth0Lock(
        APP_CONFIG.auth0ClientId,
        APP_CONFIG.auth0Domain,
        authOptions
      );

      lock.show(); //login using Auth0 Lock Login box

      if (loginAlertMessage) {
        setTimeout(() => {
          addAlertMessageToLoginScreen(loginAlertMessage);
        }, 0);
      }
    }
  }

  function logout(clearlogin = false) {
    authLoggingApiService.log('auth.service logout() called.');
    if (clearlogin) {
      tokenService.clearLastLogin();
    }
    const userProfileObject = JSON.parse(sessionService.getProfile());
    const clientId = APP_CONFIG.auth0UseRefreshToken
      ? APP_CONFIG.auth0AppV2ClientId
      : APP_CONFIG.auth0ClientId;
    let loginAuditIdentityProvider = LoginAuditIdentityProvider.UserAndPassword;
    if (sessionService.isSso()) {
      loginAuditIdentityProvider = LoginAuditIdentityProvider.SSO;
    }

    const isADFSUser =
      userProfileObject &&
      userProfileObject.identities &&
      userProfileObject.identities.length > 0 &&
      userProfileObject.identities[0].provider === 'adfs';

    authLoggingApiService.log(
      `auth.service logout() - value for isADFSUser was ${isADFSUser}`
    );

    // log user logout
    return userApiService
      .userLoginAudit({
        success: true,
        identityProvider: loginAuditIdentityProvider,
        action: LoginAuditAction.Logout
      })
      .then(logoutInner)
      .then(function () {
        sideMenuService.clearMenu();

        if (isADFSUser) {
          authLoggingApiService.log(
            `auth.service logout() userLoginAudit().then() - navigating ADFS user to https://"${APP_CONFIG.auth0Domain}/v2/logout?federated`
          );
          $window.location.href =
            'https://' + APP_CONFIG.auth0Domain + '/v2/logout?federated';
        } else {
          authLoggingApiService.log(
            'auth.service logout() userLoginAudit().then() - navigating non-ADFS user to app.login'
          );
          $window.location.href = `https://${
            APP_CONFIG.auth0Domain
          }/v2/logout?returnTo=${encodeURIComponent(
            `${window.location.origin}/login`
          )}&client_id=${clientId}`;
        }
      });
  }

  async function logoutInner() {
    authLoggingApiService.log('auth.service logout() logoutInner() called.');
    await userApiService.updateLoginState(false);
    deferredUser = $q.defer();
    userProfile = null;
    user = null;
    intercomService.shutdown();
    sessionService.clearSessionAndToken('authService.logoutInner');
    authManager.unauthenticate();
    deferredUser.resolve();
    return deferredUser.promise;
  }

  function loginWithToken(token: string, profileString: string) {
    tokenService.setToken(token, true);
    sessionService.setProfile(profileString);
    const profile = JSON.parse(profileString) as Auth0UserProfile;
    deferredProfile.resolve(profile);
    return sessionService
      .getUserDetails(null, token)
      .then(async function (response) {
        if (response === undefined) {
          return Promise.reject('Invalid user');
        }
        await loginSuccessful(response.data.dataModel);
        sessionService.setuserAuthenticated();
      });
  }

  // Set up the logic for when a user authenticates using Auth0 Lock.
  // See https://github.com/auth0/lock for a list of all events that Lock will emit during it's lifecycle
  function registerAuthenticationListener() {
    lock.on('authenticated', function (authResult) {
      // We must even run this script when we are already authenticated as some times we will be re-logging in
      // just because the user is not in local storage and so we need to get the user from the API
      sessionService.setInitialLogin();
      tokenService.setToken(authResult.idToken, true);
      authManager.authenticate();

      lock.getProfile(authResult.accessToken, function (error, profile) {
        if (error) {
          loginFailed();
          console.log(error);
        }
        // For easy access to the auth0.com flowinglyDB user record data
        sessionService.setProfile(JSON.stringify(profile));
        deferredProfile.resolve(profile);

        if (profile.identities && profile.identities.length > 0) {
          if (
            profile.identities[0].connection ===
            'Username-Password-Authentication'
          ) {
            sessionService.setIsSso(false);
            authLoggingApiService.log(
              `auth.service registerAuthenticationListener() set isSso to false and user profile identity provider was ${profile.identities[0].provider}`
            );
          } else {
            sessionService.setIsSso(true);
            authLoggingApiService.log(
              `auth.service registerAuthenticationListener() set isSso to true and user profile identity provider was ${profile.identities[0].provider}`
            );
          }
        }
      });
      if (authResult.state !== '') {
        redirectUrl = decodeURIComponent(authResult.state);
        authLoggingApiService.log(
          `auth.service registerAuthenticationListener() setting re-dir-ect-url was ${authResult.state}`
        );
      }
    });

    lock.on('authorization_error', function (authResult) {
      if (authResult.description) {
        userApiService.userLoginErrorAudit({
          success: false,
          identityProvider: LoginAuditIdentityProvider.UserAndPassword,
          error: authResult.description,
          token: APP_CONFIG.passCode,
          action: LoginAuditAction.Login
        });
        authLoggingApiService.log(
          `auth.service registerAuthenticationListener() on authorization_error. failed with error ${authResult.description}`
        );
      }
    });
  }

  function getUserPendingAcceptCondition(successCallback, failureCallback) {
    return $http
      .get<UserConditionsV2Response>(APP_CONFIG.apiBaseUrl + 'usercondition/v2')
      .then(({ data }) => {
        if (!data) {
          // At present we dont have our UserId (from our Flowingly database) stored at auth0.com
          // so am using the users email to locate him/her
          successCallback();
          return;
        }

        acceptCondition(data.termsAndCondition)
          .then(() => acceptCondition(data.announcement))
          .then(successCallback)
          .catch(failureCallback);
      });
  }

  function acceptCondition(
    condition?: UserConditionV2Detail
  ): angular.IPromise<void> {
    if (!condition) {
      return $q.resolve<void>(undefined);
    }

    return showUserConditionDialog(condition).then((accepted) => {
      if (!accepted) {
        return $q.reject();
      }

      if (!condition.id) {
        return undefined;
      }

      return $http
        .post(`${APP_CONFIG.apiBaseUrl}usercondition/${condition.id}/accept`, {
          userConditionId: condition.id
        })
        .then(() => undefined);
    });
  }

  function showUserConditionDialog(userCondition: UserConditionV2Detail) {
    return dialogService
      .showDialog({
        template:
          'Client/runner.user-condition/runner.user-condition.dialog.tmpl.html',
        controller: 'userConditionDialogController',
        appendClassName: `ngdialog-normal${
          browserUtilsService.isMobileDevice() ? ' terms-conditions-dialog' : ''
        }`,
        data: {
          ...userCondition
        },
        closeByEscape: false,
        closeByDocument: false
      })
      .then((result) => {
        if (
          result === false ||
          (result !== undefined &&
            dialogService.isCloseModalWithCancelAction(result))
        ) {
          return false;
        }

        return !!result?.accepted;
      });
  }

  function getTenants() {
    return $http
      .get<BusinessTenant[]>(`${APP_CONFIG.apiBaseUrl}business/tenants`, {
        cache: true
      })
      .then((result) => {
        return result.data;
      });
  }

  function changeTenant(tenantId: Guid) {
    return $http
      .put<IUserTokenResponse>(
        `${APP_CONFIG.apiBaseUrl}account/tenant/${tenantId}`,
        undefined
      )
      .then(async (result) => {
        await loginSuccessful(result.data.user);
        tokenService.setToken(result.data.token);
        sessionService.setuserAuthenticated();
        const settings = await sessionService.getSettings();
        APP_CONFIG.populateSettings(settings);
        APP_CONFIG.enableTenantSwitching = true;
      });
  }

  /// This API endpoint is only available in debug builds
  /// It returns a token with user permissions updated from your local database
  function getLocalOnlyToken() {
    return $http
      .get<string>(`${APP_CONFIG.apiBaseUrl}account/token`)
      .then(async (result) => {
        return result.data;
      });
  }

  //////////// Private API Methods

  //After we logon we go back to our API to get full user details
  //As we are using the Auth0 Lock library (which has its own login box - lock.show()) we cant have login also grab our user details from our API
  //So after lock authenticates the User we need to then go back through our API to get the User's details
  //In effect that is a second level of authentication - if the user is not found there or is not active then the authentication will fail
  function getUserDetails(email, token?) {
    return sessionService
      .getUserDetails(email, token)
      .then(async function (response) {
        if (response === undefined) {
          loginFailed();
        } else {
          const res = response.data;

          if (res.success === true) {
            await loginSuccessful(res.dataModel);
            // @TODO merge this and the redirect.request.service
            // We must check that it is one of our URLs before we use it. Auth0 uses the state property as part of the auth process also.
            if (
              redirectUrl.startsWith(window.location.origin) &&
              (redirectUrl.indexOf('/flows/') > 0 ||
                redirectUrl.indexOf('/categoryId?') > 0 ||
                redirectUrl.indexOf('/processmapsv2/') > 0 ||
                redirectUrl.indexOf('/modeler/') > 0 ||
                redirectUrl.indexOf('/processmaps/') > 0 ||
                redirectUrl.indexOf('/form/') !== -1)
            ) {
              authLoggingApiService.log(
                `auth.service getUserDetails() was called and about to be navigated to ${redirectUrl}`
              );
              $window.location.href = redirectUrl;
            }
          } else {
            loginFailed();
          }
        }

        return response;
      });
  }

  async function getProfileDeferred() {
    if (APP_CONFIG.auth0UseRefreshToken) {
      const client = await angularAuth0SpaSdk.getClient();
      const profile = await client.getUser();
      if (!profile) return deferredProfile.promise;
      return profile;
    }
    return deferredProfile.promise;
  }

  function getUser() {
    if (sessionService.isOldUserVersion()) {
      user.ver = sessionService.getUserVersion(); // Need to do this so we dont repeatedly get the user details until it has happened

      return sessionService
        .getUserDetails(user.email)
        .then(function (response) {
          if (response === undefined) {
            user.ver = 0; //Invalid
          } else {
            if (response.data.success === true) {
              const userDetails = sessionService.formatUserForLocalStorage(
                response.data.dataModel
              );
              sessionService.setUser(userDetails);
              $rootScope.user = userDetails;
              user = userDetails;
              return user;
            } else {
              user.ver = 0; //Invalid
            }
          }
          return user;
        });
    } else return $q.resolve(user);
  }

  function getUserDeferred() {
    return deferredUser.promise;
  }

  async function loginSuccessful(userDetail: IUserDetail) {
    authLoggingApiService.log('auth.service loginSuccessful() was called');

    if (userDetail !== undefined) {
      const localUser = sessionService.formatUserForLocalStorage(userDetail);
      sessionService.setUser(localUser);
      $rootScope.user = localUser;
      user = localUser;
      await userApiService.updateLoginState(true);
      deferredUser.resolve(localUser);
    }
  }

  function loginFailed() {
    authLoggingApiService.log('auth.service loginFailed() called');
    authManager.unauthenticate();
    userProfile = null;
    user = null;
    sessionService.clearSessionAndToken('authService.loginFailed');
    setTimeout(function () {
      authLoggingApiService.log(
        'auth.service loginFailed() about to call window.location.reload() in timeout of 3000'
      );
      window.location.reload();
    }, 3000);
  }

  /**
   * Show login alert message when user use Auth0 Lock Login box
   * @param message
   */
  function addAlertMessageToLoginScreen(message: string) {
    const authHeader =
      $window.document.getElementsByClassName('auth0-lock-header')[0];
    if (authHeader) {
      const element = document.createElement('div');
      element.style.backgroundColor = '#F46C42';
      element.style.color = 'white';
      element.style.padding = '1rem';
      element.textContent = message;
      authHeader.after(element);
    }
  }

  function auth0SpaSdkAuthenticated(idToken: string) {
    return new Promise((resolve, reject) => {
      sessionService.setInitialLogin();
      tokenService.setToken(idToken, true);
      authManager.authenticate();

      angularAuth0SpaSdk.getClient().then((auth0Client) => {
        auth0Client
          .getUser()
          .then((profile) => {
            const provider = profile.sub.split('|')[0];
            profile.identities = [];
            profile.identities[0] = { provider: provider };
            sessionService.setProfile(JSON.stringify(profile));
            deferredProfile.resolve(profile);
            const isAuth0Login = provider === 'auth0';
            sessionService.setIsSso(!isAuth0Login);
            authLoggingApiService.log(
              `auth.service auth0SpaSdkAuthenticated() set isSso to ${!isAuth0Login} and user profile identity provider was ${provider}`
            );
            resolve(profile);
          })
          .catch((error) => {
            loginFailed();
            reject(error);
          });
      });
    });
  }

  /**
   * Handle tokens received from iframe popup login.
   * Stores tokens in the iframe's partitioned localStorage and authenticates the user.
   * @param tokens The tokens received from the popup
   * @param redirectUrl Optional URL to navigate to after authentication
   */
  async function handleIframePopupTokens(
    tokens: {
      idToken: string;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    },
    redirectUrl?: string
  ): Promise<void> {
    authLoggingApiService.log(
      'auth.service handleIframePopupTokens() - Processing tokens from popup'
    );

    try {
      // Set flag to skip token refresh in interceptor - Auth0 SDK refresh fails in iframe context
      tokenService.setSkipTokenRefresh(true);

      sessionService.setInitialLogin();
      tokenService.setToken(tokens.idToken, true);
      authManager.authenticate();

      // Store the Auth0 SPA SDK cache in the iframe's partitioned localStorage
      // This allows the SDK to use refresh tokens for subsequent token renewals
      const cacheKey = `@@auth0spajs@@::${APP_CONFIG.auth0AppV2ClientId}::default::openid email offline_access nameidentifier businessidentifier flowinglypermissions`;
      const cacheEntry = {
        body: {
          id_token: tokens.idToken,
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
          token_type: 'Bearer',
          expires_in: tokens.expiresIn,
          scope:
            'openid email offline_access nameidentifier businessidentifier flowinglypermissions'
        },
        expiresAt: Math.floor(Date.now() / 1000) + tokens.expiresIn
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));

      // Decode the ID token to get user profile info
      const tokenParts = tokens.idToken.split('.');
      if (tokenParts.length < 2) {
        throw new Error('Invalid ID token format');
      }
      const base64UrlPayload = tokenParts[1];
      const base64Payload = base64UrlPayload
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const paddingLength = (4 - (base64Payload.length % 4)) % 4;
      const paddedBase64Payload = base64Payload + '='.repeat(paddingLength);
      const decodedPayloadJson = atob(paddedBase64Payload);
      const payload = JSON.parse(decodedPayloadJson);

      // Store the Auth0 SPA SDK user cache entry so isAuthenticated() returns true on page refresh
      const userCacheKey = `@@auth0spajs@@::${APP_CONFIG.auth0AppV2ClientId}::@@user@@`;
      const userCacheEntry = {
        decodedToken: {
          user: payload
        }
      };
      localStorage.setItem(userCacheKey, JSON.stringify(userCacheEntry));
      const provider = payload.sub?.split('|')[0] || 'unknown';

      const profile = {
        sub: payload.sub,
        email: payload.email,
        name: payload.name,
        identities: [{ provider: provider }]
      };

      sessionService.setProfile(JSON.stringify(profile));
      deferredProfile.resolve(profile);

      // Also store profile directly in localStorage for iframe context
      try {
        const profileStoreName =
          'flowingly.' + APP_CONFIG.flowinglyEnvironment + '.profile';
        localStorage.setItem(profileStoreName, JSON.stringify(profile));
      } catch (e) {
        console.error(
          'auth.service - Failed to store profile in localStorage',
          e
        );
      }

      const isAuth0Login = provider === 'auth0';
      sessionService.setIsSso(!isAuth0Login);

      // Fetch user details from API with direct Authorization header
      // (bypassing the interceptor which tries to refresh tokens)

      const userResponse = await $http.post<{
        success: boolean;
        dataModel: IUserDetail;
      }>(
        `${APP_CONFIG.apiBaseUrl}account/getUser`,
        { email: profile.email },
        {
          headers: {
            Authorization: `Bearer ${tokens.idToken}`
          }
        }
      );

      if (!userResponse || !userResponse.data || !userResponse.data.success) {
        throw new Error('Failed to fetch user details from API');
      }

      // Set up the user manually (similar to loginSuccessful but without updateLoginState API call)
      // The updateLoginState call would fail through the interceptor, so we skip it for iframe popup auth
      const userDetail = userResponse.data.dataModel;
      const localUser = sessionService.formatUserForLocalStorage(userDetail);
      sessionService.setUser(localUser);
      $rootScope.user = localUser;
      user = localUser;
      deferredUser.resolve(localUser);

      // Also store user directly in localStorage for iframe context
      try {
        const userStoreName =
          'flowingly.' + APP_CONFIG.flowinglyEnvironment + '.user';
        localStorage.setItem(userStoreName, JSON.stringify(localUser));
      } catch (e) {
        console.error('auth.service - Failed to store user in localStorage', e);
      }

      // Try to update login state (non-critical)
      try {
        await $http.put(`${APP_CONFIG.apiBaseUrl}account/loggedIn/true`, null, {
          headers: {
            Authorization: `Bearer ${tokens.idToken}`
          }
        });
      } catch (e) {
        console.error('auth.service - Failed to update login state', e);
      }

      // Emit authentication event
      $rootScope.$emit('flowingly.onAuthentication');

      // Navigate to the redirect URL or default page
      let targetPath = redirectUrl || '/flowsactive/';
      if (!redirectUrl && iframePopupAuthService.isInIframe()) {
        const currentPath = $location.path();
        if (
          currentPath &&
          (currentPath.startsWith('/map') ||
            currentPath.startsWith('/form') ||
            currentPath.startsWith('/sop'))
        ) {
          targetPath = currentPath;
          authLoggingApiService.log(
            `auth.service handleIframePopupTokens() - Staying on current iframe-allowed path: ${targetPath}`
          );
        } else {
          // Fallback: check if a redirectUrl was passed via the login route query params
          // This handles the case where a public controller set redirectUrl on $state.go(STATE_MAPPINGS.login)
          // but it wasn't forwarded through the popup login flow
          const queryRedirectUrl = $location.search().redirectUrl;
          if (queryRedirectUrl) {
            targetPath = queryRedirectUrl;
            authLoggingApiService.log(
              `auth.service handleIframePopupTokens() - Using redirectUrl from query params: ${targetPath}`
            );
          }
        }
      }

      try {
        if (targetPath.includes('%')) {
          targetPath = decodeURIComponent(targetPath);
        }
      } catch (e) {
        console.error('auth.service - Failed to decode redirect URL', e);
      }

      // Extract just the path from the URL (remove origin if present)
      let pathOnly = targetPath;
      if (targetPath.startsWith('http')) {
        try {
          const url = new URL(targetPath);
          pathOnly = url.pathname + url.search + url.hash;
        } catch (e) {
          console.error('auth.service - Failed to parse target URL', e);
        }
      }

      // In iframe context, use AngularJS $location navigation instead of full page reload.
      // A full page reload triggers a new HTTP request which returns CSP frame-ancestors 'self'
      // headers from the server, blocking the iframe on cross-origin parents.
      // AngularJS client-side navigation avoids the CSP check entirely.
      if (iframePopupAuthService.isInIframe()) {
        authLoggingApiService.log(
          `auth.service handleIframePopupTokens() - Using client-side navigation to: ${pathOnly}`
        );
        $rootScope.$evalAsync(() => {
          $location.url(pathOnly);
        });
      } else {
        // Use full page reload to navigate - auth data is stored in localStorage
        const fullUrl = $window.location.origin + pathOnly;
        $window.location.href = fullUrl;
      }

      // Note: skipTokenRefresh will need to be set again after reload
      // This happens automatically in runner.run.ts when iframe is detected
    } catch (error) {
      // Clear the skip flag on error so normal auth flow can retry
      tokenService.setSkipTokenRefresh(false);

      authLoggingApiService.log(
        `auth.service handleIframePopupTokens() - Error: ${
          error?.message || error
        }`
      );
      // Don't call loginFailed() as it reloads the page and causes infinite loop
      // Instead, show an error message and let the user retry
      dialogService.showMessageDialog({
        headerText: 'Login Error',
        message: `Authentication failed: ${
          error?.message || 'Unknown error'
        }. Please try again.`
      });
      throw error;
    }
  }
}

export type AuthServiceType = ReturnType<typeof authService>;
